import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Award, ArrowRight, ArrowLeft, FileDown, Loader2, CheckCircle, Eye, CalendarIcon, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import RecentAddressPicker from "@/components/certificates/RecentAddressPicker";
import { ELEC_FORMS } from "@/config/electricalFormConfigs";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import { queueForSync } from "@/utils/syncQueue";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import { authFetch } from "@/utils/authFetch";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SignaturePad from "@/components/SignaturePad";
import TemplateBar, { SaveTemplateButton } from "@/components/TemplateBar";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{children}</label>
);

function FormField({ field, value, onChange }) {
  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 py-2 cursor-pointer group">
        <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)}
          className="mt-0.5 w-5 h-5 border-2 rounded-none data-[state=checked]:bg-[#00A3E0] data-[state=checked]:border-[#00A3E0] flex-shrink-0"
          style={{ borderColor: 'var(--border-main)' }} />
        <span className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (<div className="form-field"><Label>{field.label}</Label>
      <select className="brutalist-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select...</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select></div>);
  }
  if (field.type === "textarea") {
    return (<div className="form-field"><Label>{field.label}</Label>
      <textarea className="brutalist-input min-h-[60px] resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} /></div>);
  }
  return (<div className="form-field"><Label>{field.label}</Label>
    <input className="brutalist-input" type={field.type === "number" ? "number" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} /></div>);
}

function DynamicRowTable({ section, rows, setRows }) {
  const addRow = () => {
    const empty = {};
    section.rowFields.forEach((f) => { empty[f.id] = ""; });
    setRows([...rows, empty]);
  };
  const removeRow = (idx) => { if (rows.length > 1) setRows(rows.filter((_, i) => i !== idx)); };
  const updateRow = (idx, field, val) => setRows(rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  return (
    <div>
      {rows.map((row, idx) => (
        <div key={idx} className="border-2 p-3 mb-2 flex gap-2 items-start flex-wrap" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-card)' }}>
          <span className="text-xs font-bold w-6 pt-7" style={{ color: 'var(--text-muted)' }}>{idx + 1}</span>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            {section.rowFields.map((f) => (
              <div key={f.id}>
                <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>{f.label}</label>
                {f.type === "select" ? (
                  <select className="brutalist-input !text-xs !py-1" value={row[f.id] || ""} onChange={(e) => updateRow(idx, f.id, e.target.value)}>
                    <option value="">-</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="brutalist-input !text-xs !py-1" value={row[f.id] || ""} onChange={(e) => updateRow(idx, f.id, e.target.value)} placeholder={f.placeholder || ""} />
                )}
              </div>
            ))}
          </div>
          {rows.length > 1 && <button type="button" onClick={() => removeRow(idx)} className="text-red-500 hover:text-red-700 pt-6"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
      <button type="button" onClick={addRow} className="brutalist-btn-outline !px-3 !py-1.5 flex items-center gap-1 text-xs mt-1">
        <Plus className="w-3 h-3" /> Add Row
      </button>
    </div>
  );
}

export default function GenericElecCertWizard() {
  const { formId } = useParams();
  const { user } = useAuth();
  const config = ELEC_FORMS[formId];
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const [step, setStep] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [signature, setSignature] = useState("");

  const draftKey = formId ? `draft_elec_${formId}` : null;

  const [project, setProject] = useState({
    project_ref: "", installation_address: "", installation_postcode: "",
    client_name: "", client_email: "", completed_by: user?.name || "", cert_date: null,
    contractor_trading_title: user?.company || "", contractor_email: user?.email || "",
    contractor_phone: "", contractor_address: "", contractor_postcode: "", contractor_reg_no: "",
  });
  const [fields, setFields] = useState({});
  const [dynamicRows, setDynamicRows] = useState({});

  // Auto-save draft every 10s
  useEffect(() => {
    if (!draftKey || completed) return;
    const timer = setInterval(() => {
      saveDraft(draftKey, { project, fields, dynamicRows }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [project, fields, dynamicRows, draftKey, completed]);

  const manualSaveDraft = async () => {
    if (!draftKey) return;
    await saveDraft(draftKey, { project, fields, dynamicRows });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  // Init dynamic row sections
  useEffect(() => {
    if (!config) return;
    const initRows = {};
    config.sections.forEach((s, i) => {
      if (s.hasDynamicRows) {
        const empty = {};
        s.rowFields.forEach((f) => { empty[f.id] = ""; });
        initRows[i] = [{ ...empty }];
      }
    });
    setDynamicRows(initRows);
  }, [config]);

  const [editCertId, setEditCertId] = useState(null);

  // Auto-fill contractor + load drafts + load edit data
  useEffect(() => {
    // Check for edit data first
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setProject((p) => ({ ...p, ...cert, cert_date: cert.cert_date ? new Date(cert.cert_date.split("/").reverse().join("-")) : null }));
        if (cert.form_fields) setFields(cert.form_fields);
        if (cert.dynamic_rows) setDynamicRows(cert.dynamic_rows);
        toast.info("Editing certificate — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }

    const dup = sessionStorage.getItem("duplicate_cert_data");
    if (dup) {
      try {
        const cert = JSON.parse(dup);
        sessionStorage.removeItem("duplicate_cert_data");
        setProject((p) => ({ ...p, ...cert, cert_date: null }));
        if (cert.form_fields) setFields(cert.form_fields);
        if (cert.dynamic_rows) setDynamicRows(cert.dynamic_rows);
        return;
      } catch { /* ignore */ }
    }

    const repeatData = sessionStorage.getItem("repeat_cert_data");
    if (repeatData) {
      try {
        const prefill = JSON.parse(repeatData);
        sessionStorage.removeItem("repeat_cert_data");
        setProject((p) => ({ ...p, ...prefill, cert_date: null }));
        toast.success("Customer and address details pre-filled from previous job");
        return;
      } catch { /* ignore */ }
    }
    // Try loading a saved draft
    if (draftKey) {
      loadDraft(draftKey).then((draft) => {
        if (draft) {
          if (draft.project) setProject((p) => ({ ...p, ...draft.project }));
          if (draft.fields) setFields(draft.fields);
          if (draft.dynamicRows) setDynamicRows(draft.dynamicRows);
          toast.info("Restored from saved draft");
          return;
        }
      }).catch(() => {});
    }
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved?.contractor_trading_title) setProject((p) => ({ ...p, ...Object.fromEntries(Object.entries(saved).filter(([, v]) => v)) }));
      }).catch(() => {});
  }, [draftKey]);

  // Load templates for this form type
  useEffect(() => {
    if (!config) return;
    authFetch(`${API}/api/templates?cert_type=${config.type}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { templates: [] })
      .then((d) => setTemplates(d.templates || []))
      .catch(() => {});
  }, [config]);

  const loadTemplate = (tpl) => {
    if (tpl.data?.project) setProject((p) => ({ ...p, ...tpl.data.project, cert_date: null }));
    if (tpl.data?.fields) setFields(tpl.data.fields);
    if (tpl.data?.dynamicRows) setDynamicRows(tpl.data.dynamicRows);
    toast.success(`Template "${tpl.name}" loaded`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error("Enter a template name"); return; }
    try {
      await authFetch(`${API}/api/templates`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name: templateName, cert_type: config.type, data: { project, fields, dynamicRows } }),
      });
      setTemplates((prev) => [{ name: templateName, cert_type: config.type, data: { project, fields, dynamicRows } }, ...prev]);
      setTemplateName("");
      toast.success("Template saved");
    } catch { toast.error("Failed to save template"); }
  };

  if (!config) return <Navigate to="/certificates" replace />;

  const setP = (f) => (e) => setProject((p) => ({ ...p, [f]: e.target.value }));
  const setF = (id, val) => setFields((prev) => ({ ...prev, [id]: val }));

  const accentColor = config.accent === "red" ? "#EF4444" : "#00A3E0";
  const STEPS = [{ num: 1, label: "Details & Form" }, { num: 2, label: "Review & Generate" }];

  const buildPayload = () => ({
    ...project, cert_date: project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "",
    form_type: config.type, form_title: config.title, form_fields: fields,
    signature,
    dynamic_rows: dynamicRows,
    form_sections: config.sections.map((s, si) => ({
      title: s.title, hasDynamicRows: !!s.hasDynamicRows,
      fields: s.hasDynamicRows ? [] : s.fields.map((f) => ({ id: f.id, label: f.label, type: f.type, value: fields[f.id] ?? "" })),
      rows: s.hasDynamicRows ? (dynamicRows[si] || []) : [],
      rowFields: s.hasDynamicRows ? s.rowFields.map((f) => ({ id: f.id, label: f.label })) : [],
    })),
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const previewEndpoint = `${API}/api/certificates/electrical/preview`;
      const res = await authFetch(previewEndpoint, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) {
      await queueForSync({ endpoint: "/api/certificates/electrical", method: "POST", payload: buildPayload(), formTitle: config.title });
      await manualSaveDraft();
      toast.success("You're offline — certificate queued and will auto-submit when back online.");
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/electrical`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) {
        promptBuyCredits("electrical");
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setCompleted(true);
      toast.success(isEdit ? "Certificate updated!" : "Certificate generated! 1 credit used.");
      if (draftKey) deleteDraft(draftKey).catch(() => {});
      authFetch(`${API}/api/user/contractor-details`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ contractor_trading_title: project.contractor_trading_title, contractor_email: project.contractor_email, contractor_phone: project.contractor_phone, contractor_address: project.contractor_address, contractor_postcode: project.contractor_postcode, contractor_reg_no: project.contractor_reg_no }),
      }).catch(() => {});
    } catch (err) {
      if (!navigator.onLine || err.message === "Failed to fetch") {
        await queueForSync({ endpoint: "/api/certificates/electrical", method: "POST", payload: buildPayload(), formTitle: config.title });
        toast.success("Network lost — certificate queued for auto-sync.");
      } else {
        toast.error(err.message);
      }
    } finally { setSubmitting(false); }
  };

  if (completed) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="form-container text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Certificate Generated</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Your {config.title} has been created.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
              <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="elec-cert-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Award className="w-6 h-6" style={{ color: accentColor }} />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.title}</h1>
        </div>
        <RecentAddressPicker onSelect={(a) => { setProject((p) => ({ ...p, installation_address: a.address, installation_postcode: a.postcode, client_name: a.client_name || p.client_name, client_email: a.client_email || p.client_email })); }} />

        {/* Steps */}
        <div className="flex items-center justify-between mb-8 mt-6">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold`}
                  style={{ backgroundColor: step > s.num ? accentColor : step === s.num ? accentColor : 'var(--bg-card)', color: step >= s.num ? 'white' : 'var(--text-muted)', borderColor: step > s.num ? accentColor : step === s.num ? accentColor : 'var(--border-light)' }}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className="text-xs mt-2 font-medium text-center hidden sm:block" style={{ color: step >= s.num ? 'var(--text-primary)' : 'var(--text-muted)' }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-2 sm:mx-4" style={{ backgroundColor: step > s.num ? accentColor : 'var(--border-light)' }} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="form-container max-w-3xl mx-auto">
            {/* Template Loader */}
            {templates.length > 0 && (
              <div className="border-b-2 pb-4 mb-5" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: accentColor }}>Load Template</h3>
                <div className="flex flex-wrap gap-2">
                  {templates.map((tpl, i) => (
                    <button key={i} type="button" onClick={() => loadTemplate(tpl)} data-testid={`load-template-${i}`}
                      className="brutalist-btn-outline !px-3 !py-1.5 text-xs">{tpl.name}</button>
                  ))}
                </div>
              </div>
            )}
            {/* Project Details */}
            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: accentColor }}>Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="form-field"><Label>Client name</Label><input className="brutalist-input" value={project.client_name} onChange={setP("client_name")} /></div>
                <div className="form-field"><Label>Client email</Label><input className="brutalist-input" type="email" value={project.client_email} onChange={setP("client_email")} placeholder="optional" /></div>
                <div className="form-field md:col-span-2"><Label>Installation address</Label><input className="brutalist-input" value={project.installation_address} onChange={setP("installation_address")} /></div>
                <div className="form-field"><Label>Postcode</Label><input className="brutalist-input" value={project.installation_postcode} onChange={setP("installation_postcode")} /></div>
                <div className="form-field">
                  <Label>Date</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button className="brutalist-input flex items-center justify-between cursor-pointer w-full" type="button">
                        <span style={{ color: project.cert_date ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "Select date"}
                        </span>
                        <CalendarIcon className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 rounded-none" style={{ borderColor: 'var(--border-main)' }} align="start">
                      <Calendar mode="single" selected={project.cert_date} onSelect={(d) => { setProject((p) => ({ ...p, cert_date: d })); setCalendarOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Form sections */}
            {config.sections.map((section, si) => (
              <div key={si} className="border-b-2 pb-5 mb-5 last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: accentColor }}>{section.title}</h3>
                {section.hasDynamicRows ? (
                  <DynamicRowTable section={section} rows={dynamicRows[si] || [{}]} setRows={(r) => setDynamicRows((prev) => ({ ...prev, [si]: r }))} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {section.fields.map((f) => (
                      <div key={f.id} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                        <FormField field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-between items-center">
              <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="save-draft-btn">
                {draftSaved ? "Draft Saved" : "Save Draft"}
              </button>
              <button onClick={() => setStep(2)} className="brutalist-btn flex items-center gap-2">Review <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-container max-w-3xl mx-auto">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)' }}>Review details before generating.</p>

            <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-2" style={{ color: accentColor }}>Project</h3>
              {[["Client", project.client_name], ["Address", project.installation_address], ["Postcode", project.installation_postcode],
                ["Date", project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : ""]].map(([l, v]) => v ? (
                <div key={l} className="flex justify-between py-1 text-sm"><span style={{ color: 'var(--text-secondary)' }}>{l}</span><span className="font-semibold">{v}</span></div>
              ) : null)}
            </div>

            {config.sections.map((section, si) => (
              <div key={si} className="border-b-2 pb-4 mb-4 last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-2" style={{ color: accentColor }}>{section.title}</h3>
                {section.hasDynamicRows ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead><tr style={{ backgroundColor: 'var(--border-main)', color: 'white' }}>
                        <th className="p-1.5">#</th>
                        {section.rowFields.map((f) => <th key={f.id} className="p-1.5">{f.label}</th>)}
                      </tr></thead>
                      <tbody>
                        {(dynamicRows[si] || []).map((row, ri) => (
                          <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'var(--bg-card)' : 'var(--row-alt)' }}>
                            <td className="p-1.5 text-center font-bold">{ri + 1}</td>
                            {section.rowFields.map((f) => <td key={f.id} className="p-1.5 text-center">{row[f.id] || "—"}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  section.fields.map((f) => {
                    const val = fields[f.id];
                    if (!val && val !== 0) return null;
                    return (<div key={f.id} className="flex justify-between py-1 text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
                      <span className="font-semibold text-right max-w-[60%]">{String(val)}</span>
                    </div>);
                  })
                )}
              </div>
            ))}

            {/* Save as Template */}
            <div className="border-2 p-3 mb-4 flex items-center gap-2" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--row-alt)' }}>
              <input className="brutalist-input flex-1 !py-1.5" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name..." data-testid="template-name-input" />
              <button type="button" onClick={saveTemplate} data-testid="save-template-btn"
                className="brutalist-btn-outline !px-3 !py-1.5 text-xs whitespace-nowrap">Save Template</button>
            </div>

            {/* Signature */}
            <div className="border-t-2 pt-5 mt-4" style={{ borderColor: 'var(--border-light)' }}>
              <SignaturePad label="Engineer's Signature" value={signature} onChange={setSignature} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button onClick={() => setStep(1)} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
              <div className="flex gap-3">
                <button onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
                </button>
                <button onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
      <PdfPreviewModal open={showPreview} onClose={() => { setShowPreview(false); if (previewUrl) URL.revokeObjectURL(previewUrl); }} pdfUrl={previewUrl} loading={previewing} />
    </div>
  );
}
